/**
 * The vault's cryptography. WebCrypto only — no dependencies.
 *
 * ── THE SHAPE, AND WHY ──────────────────────────────────────────────────────────────────────────
 *
 * Two keys, not one:
 *
 *   DEK — a random 256-bit AES-GCM key, one per class. It encrypts the student records.
 *   KEK — derived from the teacher's passphrase. It encrypts (wraps) the DEK, and nothing else.
 *
 * Deriving the content key straight from the passphrase, which the first draft did, is wrong for two
 * independent reasons. Changing the passphrase would mean re-encrypting every roster the teacher owns,
 * so in practice nobody would ever change it. And a co-teacher could only ever be given access by being
 * told the passphrase, which cannot be revoked and travels over text message. With the split, a
 * passphrase change re-wraps one 32-byte key, and a co-teacher later gets their own wrapped copy of the
 * same DEK.
 *
 * ── WHAT NEVER LEAVES THE BROWSER ───────────────────────────────────────────────────────────────
 *
 * The passphrase, the KEK, and the unwrapped DEK. What we store is the WRAPPED DEK and the ciphertext,
 * neither of which we can open. The salt and the KDF parameters are stored in the clear because they
 * are not secret; they are needed to re-derive the KEK on another device, which is the entire point.
 *
 * ── PER-STUDENT, NOT ONE BLOB ───────────────────────────────────────────────────────────────────
 *
 * Each student's record is encrypted separately. One blob would make every edit a whole-document
 * rewrite, so a save from a laptop would silently discard a save from a phone — and the roster is
 * exactly the thing a teacher edits from two devices in a week.
 *
 * ── AAD BINDS A RECORD TO ITS SUBJECT ───────────────────────────────────────────────────────────
 *
 * Each record's additional authenticated data is `classId:studentId:v`. Without it, ciphertext is
 * portable: anyone who can write the vault could copy one child's encrypted record over another's, and
 * decryption would succeed, silently attaching the wrong name — and therefore the wrong report card — to
 * a child. AES-GCM authenticates AAD without storing it, so a transplanted record fails to decrypt
 * instead.
 */

export const VAULT_VERSION = 1 as const;

/**
 * PBKDF2-HMAC-SHA256 at 600,000 iterations — the OWASP figure for SHA-256, about half a second to a
 * second and a half on a laptop, paid once per browser.
 *
 * Argon2id resists GPU guessing better, and a weak passphrase is the realistic failure here, but it
 * needs a WASM dependency. The parameters are recorded in the stored header instead, so moving to
 * Argon2id later re-wraps the DEK and touches no ciphertext. The stronger lever today is the recovery
 * code, which is random rather than human-chosen.
 */
export const KDF = {
  name: 'PBKDF2' as const,
  hash: 'SHA-256' as const,
  /** Used for NEW parameters. Safe to raise. */
  iterations: 600_000,
} as const;

/**
 * The floor for ACCEPTING stored parameters. **Never raise this.**
 *
 * Separate from `KDF.iterations`, and the separation is the whole point. With one constant doing both jobs,
 * raising it to strengthen new vaults would make every EXISTING teacher's stored params fall below the floor
 * — so `deriveKek` would refuse them, their vault would be permanently unopenable, and the recovery code
 * would fail identically because it derives through the same function. There is no re-wrap path out of that,
 * because re-wrapping requires deriving the old KEK, which is precisely what is being refused.
 *
 * So: this equals the lowest value ever deployed. Raising `KDF.iterations` strengthens new vaults; old ones
 * are upgraded by `changePassphrase` (which re-wraps under fresh params), decided on by `kdfNeedsUpgrade`.
 * This number stays put.
 */
export const KDF_MIN_ITERATIONS = 600_000;

/**
 * The ceiling, which exists for the opposite attack to the floor. See `deriveKek`.
 *
 * Twenty times the current standard: roughly a second of work on a modern laptop, so decades of headroom
 * for raising `KDF.iterations` and no legitimate stored document ever refused.
 */
export const KDF_MAX_ITERATIONS = 12_000_000;

/**
 * Are these stored parameters weaker than the current standard?
 *
 * A PREDICATE, not a mechanism — and the comment here used to claim otherwise. The actual upgrade path is
 * `changePassphrase`, which generates fresh params and re-wraps every class key under them; calling it with
 * the same passphrase twice is a pure re-wrap. This function exists so a caller can decide whether to offer
 * that, and a test pins that the path really produces stronger params.
 *
 * Nothing calls it yet, which is true of most of the vault — but the false claim about an automatic
 * "upgrade-on-unlock" was worth removing on its own, because it described a mechanism that did not exist.
 */
export const kdfNeedsUpgrade = (params: KdfParams): boolean =>
  params.iterations < KDF.iterations;

/** A stored KDF parameter set was rejected outright. Distinct from a wrong passphrase. */
export class WeakKdfParamsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WeakKdfParamsError';
  }
}

/** Minimum passphrase length. Short of real strength estimation, length is the honest lever. */
export const MIN_PASSPHRASE_LENGTH = 12;

export interface KdfParams {
  alg: 'PBKDF2-SHA256';
  iterations: number;
  /** base64. Not secret — it exists so the KEK can be re-derived on another device. */
  salt: string;
}

/** One encrypted student record. */
export interface SealedRecord {
  /**
   * The format version, as a plain NUMBER rather than `typeof VAULT_VERSION`.
   *
   * It was the literal type, which asserts that every value this field can ever hold is the one this
   * build writes. That is false by construction: the field is read back out of Firestore, where it can
   * hold anything a past build wrote, a future build wrote, or anyone with write access put there — and
   * the literal type made the runtime check in `openRecord` look like dead code to anyone reading it, and
   * made a test for a v99 record impossible to even write. The type now says what is true.
   */
  v: number;
  /** base64, 12 bytes, fresh per write. */
  iv: string;
  /** base64 ciphertext+tag. */
  ct: string;
  /** Bumped on every write so a stale device cannot overwrite a newer edit. */
  rev: number;
}

/**
 * A wrapped DEK. Carries NO kdf params of its own.
 *
 * The KEK is per TEACHER, not per class, so the salt and iteration count live once on the teacher's keys
 * document and every class wrap is made under the same derived KEK. The first version gave each class its
 * own salt, which meant unlocking fifteen classes was fifteen PBKDF2 derivations at 600,000 iterations —
 * about thirty seconds — and a cross-class screen like AllStudentsView could not be opened at all. One
 * derivation now opens everything.
 */
export interface WrappedDek {
  v: typeof VAULT_VERSION;
  iv: string;
  /** base64 of the wrapped DEK. */
  ct: string;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

export const b64 = (buf: ArrayBuffer | Uint8Array): string => {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]);
  return btoa(s);
};

export const unb64 = (s: string): Uint8Array => {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
};

const randomBytes = (n: number): Uint8Array => crypto.getRandomValues(new Uint8Array(n));

/** 12 bytes, random per write. Random 96-bit nonces are safe far beyond any roster's write count. */
const freshIv = () => randomBytes(12);

/**
 * The AAD that binds a record to one child in one class, at a stated format version.
 *
 * `version` is a PARAMETER rather than the module constant, and that is the whole point. It used to
 * hardcode `VAULT_VERSION`, so `openRecord` ignored `rec.v` entirely: a record claiming `v: 99` decrypted
 * as v1, and the field was decoration. Worse in the other direction — the day `VAULT_VERSION` became 2,
 * every existing v1 record would have become permanently unopenable, because the AAD used to open it
 * would no longer be the AAD it was sealed with. A version field that cannot be read is worse than none,
 * because it looks like forward compatibility.
 */
export const recordAad = (classId: string, studentId: string, version: number = VAULT_VERSION): Uint8Array =>
  enc.encode(`${classId}:${studentId}:v${version}`);

/**
 * Record format versions this build knows how to open.
 *
 * A record from a FUTURE version must fail loudly rather than fail as "wrong key" — the two call for
 * completely different responses, and one of them is "do not touch this data with this build".
 */
export const SUPPORTED_VAULT_VERSIONS: readonly number[] = [1];

export class UnsupportedVaultVersionError extends Error {
  constructor(public readonly version: unknown) {
    super(
      `This encrypted record is in vault format v${String(version)}, which this version of Level Up `
      + 'cannot read. Reload to get the latest version; do not re-save this class in the meantime.',
    );
    this.name = 'UnsupportedVaultVersionError';
  }
}

/** A fresh random salt for a new teacher. */
export const freshKdfParams = (): KdfParams => ({
  alg: 'PBKDF2-SHA256',
  iterations: KDF.iterations,
  salt: b64(randomBytes(16)),
});

/**
 * Derive the key-encryption key from a passphrase.
 *
 * `extractable: false` on the result, so the KEK cannot be read back out by any script — including ours.
 */
export async function deriveKek(passphrase: string, params: KdfParams): Promise<CryptoKey> {
  /**
   * REFUSE WEAKENED PARAMETERS.
   *
   * These come out of Firestore, and whoever can write `users/{uid}/private/vaultKeys` — the owner, or us
   * through the Admin SDK — could set `iterations: 1`. The next class wrap would reuse it, and the
   * passphrase becomes attackable offline at HMAC speed. For a guarantee we are making ABOUT OURSELVES,
   * that is the cheap, silent, deniable downgrade, so the client declines to derive from it at all.
   *
   * Raising `KDF.iterations` does NOT make old documents fail: that is checked against
   * `KDF_MIN_ITERATIONS`, which never moves. An earlier version of this comment claimed a raise "forces a
   * re-wrap", describing a path that did not exist — a raise would have locked every existing teacher out
   * permanently, recovery code included, because re-wrapping needs the old KEK this function was refusing to
   * derive. `kdfNeedsUpgrade` is the PREDICATE for deciding to upgrade; `changePassphrase` is the mechanism
   * that does it.
   */
  if (params.alg !== 'PBKDF2-SHA256') {
    throw new WeakKdfParamsError(`Unsupported vault KDF "${params.alg}". Refusing to derive a key.`);
  }
  /**
   * Checked against the FLOOR, not against the current standard — see KDF_MIN_ITERATIONS. A typed error, so
   * callers can tell "these stored parameters are not acceptable" from "that passphrase is wrong". Reporting
   * the first as the second blames the teacher for the one failure they cannot possibly have caused.
   */
  if (!Number.isInteger(params.iterations) || params.iterations < KDF_MIN_ITERATIONS) {
    throw new WeakKdfParamsError(
      `Vault KDF iterations (${params.iterations}) are below the permitted minimum of `
      + `${KDF_MIN_ITERATIONS}. Refusing to derive a key from weakened parameters.`,
    );
  }
  /**
   * AN UPPER BOUND TOO, because the same writer can attack from the other side.
   *
   * The floor stops a silent weakening. Nothing stopped the opposite: `iterations: 2_000_000_000` in the
   * keys document derives for something like an hour, with the tab pinned and no error — so every device
   * the teacher owns appears to hang on unlock, forever, and the vault is effectively destroyed without a
   * single byte of ciphertext being touched. A denial of service against the teacher's own data, written
   * by whoever could write the floor down.
   *
   * The ceiling is generous — twenty times the current standard, which is roughly a second of work — so a
   * future raise has decades of headroom and no legitimate document is ever refused.
   */
  if (params.iterations > KDF_MAX_ITERATIONS) {
    throw new WeakKdfParamsError(
      `Vault KDF iterations (${params.iterations}) exceed the permitted maximum of `
      + `${KDF_MAX_ITERATIONS}. Refusing to derive a key: this would hang every device that tried.`,
    );
  }
  /**
   * The salt has to be a string before `unb64` touches it. A non-string threw a raw TypeError from inside
   * the base64 decoder, which surfaces as neither "wrong passphrase" nor "bad parameters" and tells
   * nobody anything.
   */
  if (typeof params.salt !== 'string' || !params.salt) {
    throw new WeakKdfParamsError('The stored vault KDF salt is missing or not a string.');
  }

  const base = await crypto.subtle.importKey(
    'raw', enc.encode(passphrase), 'PBKDF2', false, ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      hash: 'SHA-256', // the only alg this function accepts; see the guard above
      salt: unb64(params.salt) as unknown as BufferSource,
      iterations: params.iterations,
    },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    /**
     * `decrypt` as well as wrap/unwrap: a passphrase change must hand the SAME DEK to a new wrap, and
     * `unwrapKey` can only produce a non-extractable key, which cannot be re-wrapped. The change path
     * therefore decrypts the wrapped bytes and re-imports them extractable. Still never extractable
     * outside that one function.
     */
    ['wrapKey', 'unwrapKey', 'decrypt'],
  );
}

/**
 * A new random data-encryption key for a class.
 *
 * `extractable: true` ONLY because wrapping it requires exporting it under the KEK; it is never exported
 * in the clear, and the wrapped form is the only thing that is stored.
 */
export async function generateDek(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

/** Wrap a DEK under a passphrase-derived KEK. */
export async function wrapDek(dek: CryptoKey, kek: CryptoKey): Promise<WrappedDek> {
  const iv = freshIv();
  const wrapped = await crypto.subtle.wrapKey('raw', dek, kek, { name: 'AES-GCM', iv: iv as unknown as BufferSource });
  return { v: VAULT_VERSION, iv: b64(iv), ct: b64(wrapped) };
}

/**
 * Unwrap a DEK. Throws on a wrong passphrase — AES-GCM's tag fails rather than yielding a wrong key,
 * which is what lets "wrong passphrase" be reported honestly instead of surfacing as garbled names.
 */
export async function unwrapDek(w: WrappedDek, kek: CryptoKey): Promise<CryptoKey> {
  return crypto.subtle.unwrapKey(
    'raw',
    unb64(w.ct) as unknown as BufferSource,
    kek,
    { name: 'AES-GCM', iv: unb64(w.iv) as unknown as BufferSource },
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Encrypt one student's record, bound to that class and student. */
export async function sealRecord(
  plain: unknown,
  dek: CryptoKey,
  args: { classId: string; studentId: string; rev: number },
): Promise<SealedRecord> {
  const iv = freshIv();
  const ct = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv as unknown as BufferSource,
      additionalData: recordAad(args.classId, args.studentId) as unknown as BufferSource,
      tagLength: 128,
    },
    dek,
    enc.encode(JSON.stringify(plain)) as unknown as BufferSource,
  );
  return { v: VAULT_VERSION, iv: b64(iv), ct: b64(ct), rev: args.rev };
}

/**
 * Decrypt one student's record.
 *
 * Throws when the record was written for a different class or student, because the AAD will not match —
 * which is the transplant it exists to prevent.
 */
export async function openRecord<T = unknown>(
  rec: SealedRecord,
  dek: CryptoKey,
  args: { classId: string; studentId: string },
): Promise<T> {
  /**
   * CHECK THE VERSION BEFORE DECRYPTING, and build the AAD from the record's own.
   *
   * Both halves matter. Reading `rec.v` means a future format fails with a message that says so, rather
   * than as an authentication failure indistinguishable from a wrong key or a tampered blob — which a
   * teacher would read as "my data is corrupt". And sealing the record's stated version into the AAD is
   * what makes a future v2 possible at all: v1 records stay openable, because the AAD used to open one is
   * the AAD it was sealed with.
   */
  if (!SUPPORTED_VAULT_VERSIONS.includes(rec.v)) throw new UnsupportedVaultVersionError(rec.v);

  const plain = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: unb64(rec.iv) as unknown as BufferSource,
      additionalData: recordAad(args.classId, args.studentId, rec.v) as unknown as BufferSource,
      tagLength: 128,
    },
    dek,
    unb64(rec.ct) as unknown as BufferSource,
  );
  return JSON.parse(dec.decode(plain)) as T;
}

/**
 * A recovery code: 20 characters from a 30-symbol alphabet, so a little over 98 bits.
 *
 * THE COMMENT HERE USED TO SAY 128 BITS, which was simply wrong: 20 x log2(30) is 98.1. Nothing depended
 * on the figure, but a stated security parameter that is off by 30 bits is the kind of thing that ends up
 * in a letter to a school board, so it says the real number.
 *
 * 98 bits is far past brute force and sits behind PBKDF2 besides. It is random rather than human-chosen,
 * which is what makes it the strong door — and the reason PBKDF2's resistance to guessing matters less
 * here than it does for the passphrase. Excludes I, L, O, U and 0/1 so it survives being written on paper
 * and retyped.
 */
const CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
const CODE_LENGTH = 20;

export function generateRecoveryCode(): string {
  /**
   * REJECTION SAMPLING, not `byte % 30`.
   *
   * 256 is not a multiple of 30: `% 30` gives the first 16 symbols a 9/256 chance and the last 14 an
   * 8/256 chance, so the code is measurably biased and its real entropy is below the figure above. The
   * bias is small and not practically exploitable here, but it is free to remove and the alternative is a
   * comment claiming a number the code does not produce.
   *
   * 240 is the largest multiple of 30 below 256, so bytes 240-255 are discarded and drawn again. Each
   * draw rejects with probability 1/16, so the loop terminates immediately in practice.
   */
  const limit = 256 - (256 % CODE_ALPHABET.length); // 240
  const symbols: string[] = [];
  while (symbols.length < CODE_LENGTH) {
    for (const byte of randomBytes(CODE_LENGTH)) {
      if (byte >= limit) continue;
      symbols.push(CODE_ALPHABET[byte % CODE_ALPHABET.length]);
      if (symbols.length === CODE_LENGTH) break;
    }
  }
  return symbols.reduce<string>(
    (out, ch, i) => out + ch + (i % 5 === 4 && i !== CODE_LENGTH - 1 ? '-' : ''),
    '',
  );
}

/** Normalise a typed recovery code: case and separators are not significant. */
export const normaliseRecoveryCode = (s: string): string =>
  s.toUpperCase().replace(/[^0-9A-Z]/g, '');
